import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  EigentumanteilMasterIdMeta,
  EigentumanteilVersionIdMeta,
  EigentumPersonSichtListEntryMeta,
  GbpfandrechtService,
  GlaeubigerSchuldnerListEntryMeta,
  GlaeubigerSchuldnerResponseMeta,
  GlaeubigerVersionDetailEntryMeta,
  GrundpfandrechtMasterIdMeta,
  PersonStammMasterId,
  PfandrechtBelastungListEntryMeta,
  PfandrechtVersionDetailEntryMeta,
  RechteIdMeta,
  SchuldnerMasterId
} from '../../../../core/gb-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { LinkComponent, NavTarget } from '../../../../shared/components/link/link.component';
import { ComparatorService } from '../../../../shared/services/comparator-service';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { Observable, Subscription } from 'rxjs';
import { MapResponse } from '../../../../core/maps-api';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { HistoryMode } from '../../../../core/services/HistoryMode';
import { ListLoader } from '../../../../shared/components/list-loader/list-loader';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../../../shared/components/list-loader/pagination-lookup-result';
import { ListLoaderPaginationBuilder } from '../../../../shared/components/list-loader/list-loader-pagination';
import { AbstractComponent } from '../../../../shared/components/abstract-component/abstract.component';
import { ListLoaderWrapperComponent } from '../../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { KompaktPersongbComponent } from '../../../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { HistoryDirective } from '../../../../shared/directives/history.directive';
import { KompaktGrundpfandrechtComponent } from '../../../../shared/kompaktdarstellung/grundpfandrecht/kompakt-grundpfandrecht.component';
import { KompaktGrundstueckComponent } from '../../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { CapSpinnerSmallComponent } from '../../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { FormatRechteIdPipe } from '../../../../shared/pipes/format/format-rechte-id.pipe';
import { TranslateNamePipe } from '../../../../shared/pipes/simple/translate-name.pipe';
import { PersonStammMasterDetailEntryMeta } from '../../../../core/personstamm-api';
import GlaeubigerrolleEnum = GlaeubigerVersionDetailEntryMeta.GlaeubigerrolleEnum;

@Component({
  selector: 'cap-personstamm-glaeubiger',
  templateUrl: './personstamm-glaeubiger.component.html',
  styleUrls: ['./personstamm-glaeubiger.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    TranslatePipe,
    KompaktPersongbComponent,
    HistoryDirective,
    LinkComponent,
    KompaktGrundpfandrechtComponent,
    KompaktGrundstueckComponent,
    CapSpinnerSmallComponent,
    FormatRechteIdPipe,
    TranslateNamePipe
  ]
})
export class PersonstammGlaeubigerComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  personStammMasterId: PersonStammMasterId;
  @Input() auskunftsperre?: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum | undefined;

  @Input()
  mitHist = true;

  response: GlaeubigerSchuldnerResponseMeta;
  viewModelGlaeubiger: Array<ViewModelRowGlaeubiger>;

  listLoader: ListLoader<ViewModelRowGlaeubiger>;
  preloadedIds: Array<SchuldnerMasterId>;

  subscription: Subscription;
  navTargets = NavTarget;
  private readonly PAGE_SIZE = 30;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private appConfigService: AppConfigService,
    protected appformatConfigService: FormatConfigService,
    public translate: TranslateService,
    private pfandrechtService: GbpfandrechtService,
    private comparatorService: ComparatorService,
    private timepointService: TimepointService
  ) {
    super();
    keysToTranslate<string>([]);
  }

  get ANDERE_ROLLE(): GlaeubigerrolleEnum {
    return GlaeubigerVersionDetailEntryMeta.GlaeubigerrolleEnum.ANDEREROLLE;
  }

  ngOnInit(): void {
    this.viewModelGlaeubiger = [];
    this.subscription = this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
      const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
      this.pfandrechtService
        .loadPfandrechtIdsSichtPersonstamm(
          this.personStammMasterId.id,
          'GLAEUBIGER',
          this.timepointService.timepointCtx.timepointIso,
          null,
          historisierung
        )
        .subscribe((data) => {
          this.preloadedIds = data.glaeubigerMasterIds;
          this.loadGlaeubiger();
        });
    });
  }

  getDetailNavigation(): NavTarget {
    return NavTarget.NAV_GRUNDSTUECK_PFANDRECHTE;
  }

  getQueryParams(vm: ViewModelRowGlaeubiger) {
    return {
      id: vm.masterId.id,
      versionId: vm.pfandrechtVersion.id.id,
      histEnum: vm.pfandrechtVersion.histInfo.histEnum
    };
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private loadGlaeubiger() {
    const observable = (offset, limit, sortColumn) => this.createObservable(offset, limit);

    this.listLoader = ListLoaderPaginationBuilder.builder<ViewModelRowGlaeubiger>()
      .setNoSortModel(true)
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(this.listLoader);
  }

  private createObservable(offset: number, limit: number): Observable<any> {
    const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
    const batchedIds = this.preloadedIds.slice(offset, offset + limit).map((id) => id.id);

    return this.pfandrechtService
      .loadPfandrechtSichtPersonstamm(
        this.personStammMasterId.id,
        batchedIds,
        [],
        'GLAEUBIGER',
        this.timepointService.timepointCtx.timepointIso,
        null,
        historisierung
      )
      .pipe(
        map((data) => {
          this.viewModelGlaeubiger = data.results
            .map((d) => new ViewModelRowGlaeubiger(d, this.comparatorService))
            .sort((a, b) => {
              return this.comparatorService.orderByComparator(
                a.glaeubiger[0].histInfo.erstellzeitpunkt,
                b.glaeubiger[0].histInfo.erstellzeitpunkt
              );
            });
          return <PaginationLookupResult<EigentumPersonSichtListEntryMeta>>{
            result: this.viewModelGlaeubiger,
            totalItems: this.preloadedIds.length
          };
        })
      );
  }
}

class ViewModelRowGlaeubiger {
  masterId: GrundpfandrechtMasterIdMeta;
  glaeubiger: Array<GlaeubigerVersionDetailEntryMeta>;
  rechteId: RechteIdMeta;
  id: EigentumanteilMasterIdMeta;
  versionId: EigentumanteilVersionIdMeta;
  rechtArt: GlaeubigerSchuldnerListEntryMeta.RechtArtEnum;
  pfandrechtVersion: PfandrechtVersionDetailEntryMeta;
  belastungen: Array<ViewModelRowPfandrechtBelastung>;

  constructor(
    entry: GlaeubigerSchuldnerListEntryMeta,
    private comparatorService: ComparatorService
  ) {
    this.masterId = entry.id;
    this.glaeubiger = entry.glaeubiger.sort((a, b) =>
      this.comparatorService.orderByComparator(a.histInfo.erstellzeitpunkt, b.histInfo.erstellzeitpunkt)
    );
    this.rechteId = entry.rechteId;
    this.rechtArt = entry.rechtArt;
    this.pfandrechtVersion = entry.version;
    this.belastungen = [];
    entry.belastungen.forEach((b) => this.belastungen.push(new ViewModelRowPfandrechtBelastung(b)));
    this.belastungen.sort((a, b) => this.comparatorService.grundstueckComparator(a.belastung.grundstueck, b.belastung.grundstueck));
  }
}

class ViewModelRowPfandrechtBelastung {
  belastung: PfandrechtBelastungListEntryMeta;
  mapLinkItem: MapResponse;

  constructor(belastung: PfandrechtBelastungListEntryMeta) {
    this.belastung = belastung;
    this.mapLinkItem = <MapResponse>{ url: null };
  }
}
